"use client";

// react
import { toast } from "react-hot-toast";

// third party
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// anivive
import { Text } from "@anivive/ui";

// services
import { Pet } from "@/services/api/server/pets";

// private components
import Buttons from "../../components/buttons";
import ContactInfo from "../../components/contact-info";
import PetInfo from "../../components/pet-info";
import Wizard from "../../components/wizard";
import { Form, useWizard } from "../../context";

// private
import styles from "./index.module.scss";

type Props = {
  onComplete: (data: Form) => Promise<{ id: number } | null>;
  pets: Pet[];
};

export default function ReviewAnswers({ onComplete, pets }: Props) {
  const { state, trial, actions, questions } = useWizard();

  actions.goBackCheck(5);

  const firstStepOffset = trial.practices.length > 1 ? 0 : 1;

  const renderEditIcon = (step: number) => (
    <FontAwesomeIcon
      onClick={() => actions.go(step - firstStepOffset)}
      icon={faPen}
      className="ms-2 cursor--pointer"
      size="sm"
    />
  );

  const handleSubmit = async () => {
    const formEntry = await onComplete(state.data);

    if (formEntry?.id) {
      return actions.complete();
    }

    toast.error(
      <div>
        <p className="text-style-body1-bold">Error Occurred</p>
        <small>An error occurred while trying to submit your application. Please try again.</small>
      </div>,
    );
  };

  const { answers, pet } = state.data;

  return (
    <div className={styles.review}>
      {state.data.trialPractice && (
        <div>
          <h3 className="text-style-h6 mb-3">
            Study Site {trial.practices.length > 1 && renderEditIcon(1)}
          </h3>
          <div>
            <p className="text-style-body1-regular mb-1">Study Location</p>
            <Text
              value={state.data.trialPractice.name}
              className="w-100"
              disabled
              onChange={() => null}
            />
          </div>
        </div>
      )}
      <div>
        <h3 className="text-style-h6 mb-3">Pet Information {renderEditIcon(2)}</h3>
        <PetInfo pets={pets} review />
      </div>
      <div>
        <h3 className="text-style-h6 mb-3">Medical Information {renderEditIcon(3)}</h3>
        <div>
          <Wizard
            questions={questions ?? []}
            answers={answers ?? {}}
            disabled
            onChange={() => null}
          />
          <div>
            <p className="text-style-body1-regular mb-1">Pet Practice(s)</p>
            {pet?.practices && pet?.practices.length ? (
              <ol>
                {pet.practices.map(({ name }, index) => (
                  <li key={index}>{name}</li>
                ))}
              </ol>
            ) : (
              "None Selected"
            )}
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-style-h6 mb-3">Contact Information {renderEditIcon(4)}</h3>
        <ContactInfo review />
      </div>

      <Buttons onSubmit={handleSubmit} />
    </div>
  );
}
